/**
 * Backend Sync Service
 *
 * Handles all API calls to the backend for storing verification results.
 * All operations are non-blocking and fail silently to not interrupt the user flow.
 */

import { dataURLtoBlob } from '../utils/image-utils';

/**
 * API configuration.
 */
export interface ApiConfig {
  apiUrl: string;
  apiKey: string;
  sessionId: string | null;
}

/**
 * Liveness result actions mapping.
 */
export interface LivenessActions {
  blink?: boolean;
  smile?: boolean;
  turn_left?: boolean;
  turn_right?: boolean;
  open_mouth?: boolean;
  look_up?: boolean;
  look_down?: boolean;
  raise_brows?: boolean;
}

/**
 * Selfie upload result.
 */
export interface SelfieUploadResult {
  selfie1Url: string;
  selfie2Url: string;
}

/**
 * Document polling result.
 */
export interface DocumentPollingResult {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  result?: unknown;
  error?: string;
}

/**
 * Configuration for polling.
 */
export interface PollingConfig {
  initialIntervalMs: number;
  maxIntervalMs: number;
  backoffFactor: number;
  maxAttempts: number;
  jitter: boolean;
}

/**
 * Default polling configuration.
 */
const DEFAULT_POLLING_CONFIG: PollingConfig = {
  initialIntervalMs: 1000,
  maxIntervalMs: 30000,
  backoffFactor: 1.5,
  maxAttempts: 60,
  jitter: true,
};

/**
 * BackendSyncService handles API calls to the verification backend.
 *
 * Features:
 * - Non-blocking storage of verification results
 * - Selfie upload with presigned URLs
 * - Document OCR polling with exponential backoff
 * - Session file cleanup
 */
export class BackendSyncService {
  private apiUrl: string;
  private apiKey: string;
  private sessionId: string | null;
  private pollingConfig: PollingConfig;

  // Stored selfie URLs after upload
  private selfie1Url: string | null = null;
  private selfie2Url: string | null = null;

  constructor(config: ApiConfig, pollingConfig: Partial<PollingConfig> = {}) {
    this.apiUrl = config.apiUrl;
    this.apiKey = config.apiKey;
    this.sessionId = config.sessionId;
    this.pollingConfig = { ...DEFAULT_POLLING_CONFIG, ...pollingConfig };
  }

  /**
   * Updates the API configuration.
   */
  setConfig(config: Partial<ApiConfig>): void {
    if (config.apiUrl !== undefined) this.apiUrl = config.apiUrl;
    if (config.apiKey !== undefined) this.apiKey = config.apiKey;
    if (config.sessionId !== undefined) this.sessionId = config.sessionId;
  }

  /**
   * Checks if the service is in demo mode (should skip API calls).
   */
  private isDemoMode(): boolean {
    return !this.sessionId || this.apiKey === 'demo';
  }

  /**
   * Makes a fetch request with common headers.
   */
  private async fetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = {
      'X-API-Key': this.apiKey,
      'Content-Type': 'application/json',
      ...options.headers,
    };

    return fetch(url, { ...options, headers });
  }

  /**
   * Stores liveness result to backend.
   * Called after liveness detection completes successfully.
   */
  async storeLivenessResult(passed: boolean, actions?: LivenessActions): Promise<void> {
    if (this.isDemoMode()) {
      console.log('[BackendSync] Skipping liveness storage (demo mode)');
      return;
    }

    try {
      console.log('[BackendSync] Storing liveness result:', { passed, actions });

      const payload: { passed: boolean; actions?: LivenessActions } = { passed };
      if (actions) {
        payload.actions = actions;
      }

      const response = await this.fetch(`/v1/sessions/${this.sessionId}/liveness`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[BackendSync] Liveness result stored:', result);
      } else {
        console.warn('[BackendSync] Failed to store liveness result:', response.status);
      }
    } catch (error) {
      console.warn('[BackendSync] Error storing liveness result:', error);
      // Non-blocking - continue with flow
    }
  }

  /**
   * Stores age estimation result to backend.
   * Called after age estimation completes.
   */
  async storeAgeEstimation(age: number, confidence?: number): Promise<void> {
    if (this.isDemoMode()) {
      console.log('[BackendSync] Skipping age estimation storage (demo mode)');
      return;
    }

    try {
      console.log('[BackendSync] Storing age estimation:', { age, confidence });

      const payload: { estimated_age: number; confidence?: number } = {
        estimated_age: Math.round(age),
      };
      if (confidence !== undefined) {
        payload.confidence = confidence;
      }

      const response = await this.fetch(`/v1/sessions/${this.sessionId}/age-estimation`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[BackendSync] Age estimation stored:', result);
      } else {
        console.warn('[BackendSync] Failed to store age estimation:', response.status);
      }
    } catch (error) {
      console.warn('[BackendSync] Error storing age estimation:', error);
      // Non-blocking - continue with flow
    }
  }

  /**
   * Uploads both selfies to S3 for training data collection.
   * Called after age estimation completes.
   */
  async uploadSelfies(
    selfie1DataUrl: string,
    selfie2DataUrl: string,
    estimatedAge: number
  ): Promise<SelfieUploadResult | null> {
    if (this.isDemoMode()) {
      console.log('[BackendSync] Skipping selfie upload (demo mode)');
      return null;
    }

    try {
      console.log('[BackendSync] Getting presigned URLs...');

      // 1. Get presigned URLs
      const presignResponse = await this.fetch('/v1/ocr/presign-selfies', {
        method: 'POST',
        body: JSON.stringify({
          verification_session_id: this.sessionId,
          self_declared_age: 0,
          estimated_age: Math.round(estimatedAge) || 0,
        }),
      });

      if (!presignResponse.ok) {
        console.warn('[BackendSync] Failed to get presigned URLs:', presignResponse.status);
        return null;
      }

      const presignData = await presignResponse.json();
      console.log('[BackendSync] Got presigned URLs');

      // 2. Upload both selfies in parallel
      const selfie1Blob = dataURLtoBlob(selfie1DataUrl);
      const selfie2Blob = dataURLtoBlob(selfie2DataUrl);

      const [upload1, upload2] = await Promise.all([
        fetch(presignData.selfie1_upload_url, {
          method: 'PUT',
          body: selfie1Blob,
          headers: { 'Content-Type': selfie1Blob.type },
        }),
        fetch(presignData.selfie2_upload_url, {
          method: 'PUT',
          body: selfie2Blob,
          headers: { 'Content-Type': selfie2Blob.type },
        }),
      ]);

      if (!upload1.ok || !upload2.ok) {
        console.warn('[BackendSync] Failed to upload selfies:', upload1.status, upload2.status);
        return null;
      }

      console.log('[BackendSync] Selfies uploaded successfully');

      // Store URLs for later use
      this.selfie1Url = presignData.selfie1_url;
      this.selfie2Url = presignData.selfie2_url;

      return {
        selfie1Url: presignData.selfie1_url,
        selfie2Url: presignData.selfie2_url,
      };
    } catch (error) {
      console.warn('[BackendSync] Error uploading selfies:', error);
      return null;
    }
  }

  /**
   * Gets the stored selfie URLs.
   */
  getSelfieUrls(): SelfieUploadResult | null {
    if (!this.selfie1Url || !this.selfie2Url) return null;
    return {
      selfie1Url: this.selfie1Url,
      selfie2Url: this.selfie2Url,
    };
  }

  /**
   * Polls for document OCR result with exponential backoff.
   */
  async pollDocumentResult(
    taskId: string,
    onProgress?: (attempt: number, status: string) => void
  ): Promise<DocumentPollingResult> {
    let interval = this.pollingConfig.initialIntervalMs;
    let attempts = 0;

    while (attempts < this.pollingConfig.maxAttempts) {
      attempts++;

      try {
        const response = await this.fetch(`/v1/ocr/status/${taskId}`);

        if (!response.ok) {
          console.warn('[BackendSync] OCR status check failed:', response.status);
          onProgress?.(attempts, 'error');
          // Continue polling on non-fatal errors
        } else {
          const data = await response.json();
          onProgress?.(attempts, data.status);

          if (data.status === 'completed') {
            console.log('[BackendSync] OCR completed:', data);
            return { status: 'completed', result: data.result };
          }

          if (data.status === 'failed') {
            console.log('[BackendSync] OCR failed:', data);
            return { status: 'failed', error: data.error };
          }
        }
      } catch (error) {
        console.warn('[BackendSync] OCR polling error:', error);
        onProgress?.(attempts, 'error');
      }

      // Wait before next attempt with exponential backoff
      await this.sleep(interval);

      // Calculate next interval
      interval = Math.min(
        interval * this.pollingConfig.backoffFactor,
        this.pollingConfig.maxIntervalMs
      );

      // Add jitter if enabled
      if (this.pollingConfig.jitter) {
        interval *= 0.8 + Math.random() * 0.4;
      }
    }

    console.warn('[BackendSync] OCR polling timed out after', attempts, 'attempts');
    return { status: 'failed', error: 'Polling timeout' };
  }

  /**
   * Deletes all S3 files for the current session.
   * Called when user declines consent.
   */
  async deleteSessionFiles(): Promise<void> {
    if (this.isDemoMode()) {
      console.log('[BackendSync] Skipping file deletion (demo mode)');
      return;
    }

    try {
      console.log('[BackendSync] Deleting S3 files for session:', this.sessionId);

      const response = await this.fetch(`/v1/sessions/${this.sessionId}/files`, {
        method: 'DELETE',
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[BackendSync] Files deleted:', result);
      } else {
        console.warn('[BackendSync] Failed to delete files:', response.status);
      }
    } catch (error) {
      console.warn('[BackendSync] Error deleting files:', error);
      // Non-blocking - continue with flow
    }
  }

  /**
   * Helper to sleep for a given number of milliseconds.
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Resets stored state.
   */
  reset(): void {
    this.selfie1Url = null;
    this.selfie2Url = null;
  }
}

/**
 * Creates a BackendSyncService from URL parameters.
 */
export function createBackendSyncService(
  apiUrl: string,
  apiKey: string,
  sessionId: string | null
): BackendSyncService {
  return new BackendSyncService({ apiUrl, apiKey, sessionId });
}
